import { Colors } from "../../config/DesignConstants";
import Entity from "../../core/entity/Entity";
import { choose } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { LevelEntity } from "../LevelEntity";
import { FighterEnemy } from "./FighterEnemy";
import { FormationUtils } from "./util/FormationUtils";

const FIGHTERS_PER_SQUADRON = 16;
const SPAWN_DELAY = 0.2; // Seconds between spawning each fighter
const FORMATION_DISTANCE = 150; // Distance from center for bombing formation
const BOMBING_DURATION = 16; // Seconds to stay in bombing formation
const CASUALTY_RETREAT_THRESHOLD = 0.4; // Retreat when 40% casualties
const ESCAPE_DISTANCE = 600; // Distance to escape before self-destruct
const FORMATION_SPACING = 10; // Spacing between fighters in formation

enum SquadronPhase {
  APPROACH = "approach",
  BOMBING = "bombing",
  RETREAT = "retreat",
}

export class FighterSquadron extends LevelEntity implements Entity {
  tags = ["fighterSquadron"];
  private spawnPosition: V2d;
  private fighters: FighterEnemy[] = [];
  private phase = SquadronPhase.APPROACH;

  // Squadron configuration - simplified to just the essentials
  private squadronConfig: {
    color: number;
    munitionType: "bomb" | "missile";
  };

  // Phase tracking
  private bombingStartTime?: number;
  private initialSquadronSize = FIGHTERS_PER_SQUADRON;
  private formationPositions: V2d[] = [];

  constructor(spawnPosition: V2d) {
    super();
    this.spawnPosition = spawnPosition.clone();

    // All fighters in squadron will share these characteristics
    this.squadronConfig = {
      color: choose(Colors.PINK, Colors.MAGENTA),
      munitionType: choose("bomb", "missile"),
    };

    // Generate formation positions using utility
    const center = V(0, 0);
    const angleToSpawn = Math.atan2(spawnPosition.y, spawnPosition.x);
    const formationCenter = center.add(
      V(Math.cos(angleToSpawn), Math.sin(angleToSpawn)).mul(FORMATION_DISTANCE),
    );

    this.formationPositions = FormationUtils.generateRectangularFormation(
      formationCenter,
      FIGHTERS_PER_SQUADRON,
      FORMATION_SPACING,
    );
  }

  async onAdd(): Promise<void> {
    // Spawn all fighters with delay
    for (let i = 0; i < FIGHTERS_PER_SQUADRON; i++) {
      await this.spawnFighter(i);
      if (i < FIGHTERS_PER_SQUADRON - 1) {
        await this.wait(SPAWN_DELAY);
      }
    }

    // Monitor squadron lifecycle
    this.monitorSquadron();
  }

  private async spawnFighter(index: number): Promise<void> {
    if (!this.game) return;

    // Get formation position for this fighter
    const formationPos = this.formationPositions[index];

    const approachWaypoints = FormationUtils.generateApproachWaypoints(
      this.spawnPosition,
      formationPos,
      40,
    );

    const fighter = new FighterEnemy(
      this.spawnPosition,
      approachWaypoints,
      this.squadronConfig.color,
      this.squadronConfig.munitionType,
    );

    this.fighters.push(fighter);
    this.getCurrentLevel()?.addChild(fighter);
  }

  private async monitorSquadron(): Promise<void> {
    if (!this.game) return;

    // Wait for all fighters to reach formation
    await this.waitUntil(() =>
      this.fighters.every((f) => f.isDestroyed() || f.hasReachedFormation()),
    );

    // Start bombing phase
    this.phase = SquadronPhase.BOMBING;
    this.bombingStartTime = this.game.elapsedTime;

    // Notify all fighters of phase change
    for (const fighter of this.fighters) {
      fighter.setPhase(SquadronPhase.BOMBING);
    }

    // Wait for bombing phase to end
    await this.waitUntil(() => {
      if (!this.game) return true;

      const survivingFighters = this.fighters.filter((f) => !f.isDestroyed());
      const casualtyRate =
        1 - survivingFighters.length / this.initialSquadronSize;
      const bombingDuration = this.game.elapsedTime - this.bombingStartTime!;

      return (
        casualtyRate >= CASUALTY_RETREAT_THRESHOLD ||
        bombingDuration >= BOMBING_DURATION
      );
    });

    // Start retreat phase
    this.phase = SquadronPhase.RETREAT;
    this.startRetreat();

    // Wait for all fighters to escape or be destroyed
    await this.waitUntil(() => this.fighters.every((f) => f.isDestroyed()));

    this.destroy();
  }

  private startRetreat(): void {
    this.fighters.forEach((fighter, index) => {
      if (!fighter.isDestroyed()) {
        const retreatWaypoints = this.generateRetreatPath(fighter, index);
        fighter.setRetreatPath(retreatWaypoints);
        fighter.setPhase(SquadronPhase.RETREAT);
      }
    });
  }

  private generateRetreatPath(fighter: FighterEnemy, index: number): V2d[] {
    if (!fighter.body) return [];

    const currentPos = V(fighter.body.position);

    // Exit in opposite direction from center
    const awayFromCenter = currentPos.normalize();
    const exitPoint = awayFromCenter.mul(ESCAPE_DISTANCE);

    // Generate simple direct retreat waypoints with slight variation per fighter
    return FormationUtils.generateApproachWaypoints(currentPos, exitPoint, 30);
  }

  getCurrentPhase(): SquadronPhase {
    return this.phase;
  }
}
